import { Stack, Typography, Grid, TextField } from '@mui/material';
import { CustomTooltip } from '../CustomTooltip/CustomTooltip';
import { CheckCircleOutlineRounded, InfoOutlined } from '@mui/icons-material';
import { DocusealForm } from '@docuseal/react';
import { useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import formsService from '../../api/services/forms';
import LoadingButton from '@mui/lab/LoadingButton';
import { offers } from '@prisma/client';

interface Props {
  formik: any;
  offer: offers;
  showLeadForm: boolean;
  setFormVisible: (visible: boolean) => void;
}

const Disclosures: React.FC<Props> = ({
  formik,
  offer,
  showLeadForm,
  setFormVisible,
}) => {
  const SELLER_CLOSING_COST = 3;

  const costsSigned = formik.values.costsSigned;
  const [loading, setLoading] = useState(false);
  const [slug, setSlug] = useState('');
  const [formId, setFormId] = useState<number | null>(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (formId === SELLER_CLOSING_COST) {
      formik.setFieldValue('costsSigned', null);
    }
  }, [slug]);

  useEffect(() => {
    if (formId) {
      getForm(formId);
      setFormVisible(true);
    } else {
      setSlug('');
      setFormVisible(false);
    }
  }, [formId]);

  const getForm = async (formId: number) => {
    setLoading(true);
    const res = await formsService.post({
      listingId: offer.listingId,
      formId,
      offerId: offer.id,
    });
    setLoading(false);
    setSlug(res);
  };

  if (slug !== '') {
    return (
      <DocusealForm
        src={`https://docuseal.co/s/${slug}`}
        email="trey@homepoint.co"
        onComplete={(data) => {
          if (formId === SELLER_CLOSING_COST) {
            formik.setFieldValue('costsSigned', true);
          }
          setFormId(null);
        }}
        expand={false}
        withFieldNames={false}
        values={{
          agentInitials: 'HP',
          sellerDate: format(new Date(), 'MM/dd/yyyy'),
        }}
        readonlyFields={['agentInitials', 'sellerDate']}
        withTitle={false}
        signature={`${currentUser?.firstName} ${currentUser?.lastName}`}
      />
    );
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <Stack direction="column" spacing={0}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <LoadingButton
              variant="contained"
              onClick={() => {
                if (!costsSigned) {
                  setFormId(SELLER_CLOSING_COST);
                }
              }}
              loading={loading}
              color={costsSigned ? 'success' : 'primary'}
            >
              Sign Estimated Closing Costs{' '}
              {costsSigned ? (
                <>
                  <CheckCircleOutlineRounded sx={{ ml: 1 }} />
                </>
              ) : (
                ''
              )}
            </LoadingButton>
            <Typography variant="body2" color="error" pt={1}>
              {formik.touched.costsSigned && formik.errors.costsSigned}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} pb={1}>
              <Typography fontWeight={500}>Disclosures</Typography>
              <CustomTooltip
                width={'300'}
                placement="right"
                title={
                  <>
                    <Typography variant="body2">
                      Upfront disclosures are optional, but we recommend
                      disclosing any item that meets the critia below.
                    </Typography>
                    <ul>
                      <li>
                        Structural Issues: Any problems with the foundation,
                        roof, walls, or other structural components.
                      </li>
                      <li>
                        Water Damage: History of flooding, leaks, or water
                        damage.
                      </li>
                      <li>
                        Pest Infestations: Evidence of termites or other pest
                        problems.
                      </li>
                      <li>
                        Electrical and Plumbing Systems: Issues with wiring,
                        pipes, or major systems.
                      </li>
                      <li>
                        Environmental Hazards: Presence of lead-based paint,
                        asbestos, or radon.
                      </li>
                      <li>
                        Legal Issues: Any disputes, liens, or zoning violations
                        affecting the property.
                      </li>
                      <li>
                        Renovations and Repairs: Details of significant repairs
                        or renovations, including any that may have been
                        performed without proper permits.
                      </li>
                    </ul>
                  </>
                }
              >
                <InfoOutlined />
              </CustomTooltip>
            </Stack>
            <TextField
              fullWidth
              id="disclosureDescription"
              name="disclosureDescription"
              placeholder="If you have no disclosures, please enter None."
              multiline
              rows={2}
              value={formik.values.disclosureDescription}
              onChange={formik.handleChange}
              error={
                formik.touched.disclosureDescription &&
                Boolean(formik.errors.disclosureDescription)
              }
              helperText={
                formik.touched.disclosureDescription &&
                formik.errors.disclosureDescription
              }
            />
          </Grid>
        </Grid>
      </Stack>
    </form>
  );
};

export default Disclosures;
